import { useMutation } from "@tanstack/react-query";
import { post } from "api/api-methods";
import { WebBookingErrorCode } from "context/error-context";
import { useBookingId } from "hooks/use-booking-id";
import { useBookingStateContext } from "hooks/use-booking-state";
import { useError } from "hooks/use-error";
import queryClient from "query-client";
import { useNavigate, useParams } from "react-router-dom";
import { getSearchParams } from "search-params";
import {
    BackFromPaymentTerminalParameters,
    BackFromTerminalResponse,
} from "types/request-parameters";
import { ValidationResult } from "types/validation-result";
import { TrackingEventType } from "logger/event-utils";
import { sendToParent } from "logger/logger";
import {
    filterSearchParams,
    PAYMENT_TERMINAL_PARAMS,
    TRANSIENT_BOOKING_PARAMS,
} from "search-params/filter-search-params";

export const useBackFromPaymentTerminal = () => {
    const { system } = useParams();
    const navigate = useNavigate();
    const { setErrorCode, setValidationResult } = useError();
    const state = useBookingStateContext();
    const bookingId = useBookingId();
    const { webBookingId } = getSearchParams();
    const paramOrRouteId = bookingId ?? webBookingId;

    const backFromPaymentTerminal = async ({
        chargeId,
        offerStartTime,
    }: {
        chargeId: string;
        offerStartTime: string | null;
    }) => {
        const data: BackFromPaymentTerminalParameters = {
            bookingChargeId: chargeId,
            startTime: offerStartTime,
        };

        const eventName = "BackFromPaymentTerminal";
        let response: BackFromTerminalResponse | undefined;

        try {
            response = await post<BackFromTerminalResponse, BackFromPaymentTerminalParameters>(
                `WebBooking/WebBookings/${paramOrRouteId}/BackFromPaymentTerminal`,
                data
            );
            await queryClient.invalidateQueries({
                queryKey: ["booking", paramOrRouteId],
            });
        } catch (error) {
            const { offerStartTime } = getSearchParams();
            const errorResponse = (error as { response?: { data?: BackFromTerminalResponse } })
                ?.response?.data;
            const validationResult = new ValidationResult(errorResponse?.validationResult);
            const isWaitList = offerStartTime;
            if (validationResult.isDoubleBookingException() && isWaitList) {
                const urlDbe = `/booking/${system}/${paramOrRouteId}`;
                void navigate(urlDbe, {
                    state: { failedPaymentConfirmWaitList: true },
                });
            }
            // If request fails - set generic payment error code
            setErrorCode(WebBookingErrorCode.PaymentError);
            sendToParent(state, TrackingEventType.bookingFinalizedFailed);
            throw error;
        }

        // TODO: renable tracking
        // const endTime = new Date().getTime();
        // const loadTime = endTime - startTime;

        // const { guests, guestsChildren, start } = response.result;
        // trackEvent(eventName, {
        //     guests: guests,
        //     children: guestsChildren,
        //     date: start.toISOString(),
        //     ms: loadTime,
        // });
        sendToParent(
            state,
            eventName === "BackFromPaymentTerminal"
                ? TrackingEventType.backFromPaymentTerminal
                : TrackingEventType.backFromPaymentTerminalFailed
        );

        return response;
    };

    return useMutation({
        mutationFn: backFromPaymentTerminal,
        scope: {
            id: "webbooking-create",
        },
        onSuccess(response) {
            const validationResult = new ValidationResult(response?.validationResult);
            if (response && validationResult.isValid()) {
                const searchParamsToKeep = filterSearchParams(window.location.search, [
                    ...TRANSIENT_BOOKING_PARAMS,
                    ...PAYMENT_TERMINAL_PARAMS,
                ]);
                const pathname = `/booking/${system}${state.isActivityBooking ? "/activity-booking" : ""}/${paramOrRouteId}`;
                void navigate(
                    { pathname, search: searchParamsToKeep ? `?${searchParamsToKeep}` : "" },
                    { state: { isFinal: true } }
                );
                sendToParent(state, TrackingEventType.bookingFinalized);
            } else {
                const validationResult = new ValidationResult(response?.validationResult);
                setValidationResult(validationResult);
                // If request fails - set generic payment error code
                setErrorCode(WebBookingErrorCode.PaymentError);
                sendToParent(state, TrackingEventType.bookingFinalizedFailed);
            }
        },
    });
};
